export const purchaseUrl = 'https://clear-the-clutter.impultienda.ar/checkout'

export const pricingAnchor = '#pricing'

export const mockupUrl =
  'https://cdn.magicpatterns.com/uploads/66dE3booDsTZrKbidzX3uZ/Imagen_de_ChatGPT_4_oct_2026,_06_46_49_p.m.png'

/* ---------- Pricing math (USD) ---------- */
export const volumesValue = 26.9
export const bonusValues = [12, 10, 15, 12, 10]
export const totalBonusValue = bonusValues.reduce((sum, value) => sum + value, 0) // 59
export const totalValue = volumesValue + totalBonusValue // 85.90
export const todayPriceValue = 26.9
export const savingsValue = totalValue - todayPriceValue // 59
export const savingsPercent = Math.round((savingsValue / totalValue) * 100) // 69

export function usd(value: number) {
  return `$${Number.isInteger(value) ? value : value.toFixed(2)} USD`
}

/* ---------- Hero ---------- */
export const heroStats = [
  ['5', 'Complete Volumes'],
  ['5', 'Practical Bonuses'],
  ['10', 'Complete Guides'],
  ['PDF', 'Immediate Digital Access'],
] as const

export const heroBadges = [
  '100% Digital PDF',
  'Access in minutes',
  '5 Complete Volumes',
  '5 Practical Bonuses',
  'Real household categories',
]

export const clutterTypes = [
  'Clothes you never wear.',
  'Cables you cannot identify.',
  'Kitchen cabinets full of duplicates.',
  'Boxes you have not opened in years.',
  'Things waiting to be sold.',
  'Donation bags that never leave.',
]

export const heroNegations = [
  'No full-house cleanout.',
  'No complicated organizing method.',
  'No pressure to get rid of everything.',
]

/* ---------- The real problem ---------- */
export const excuses = [
  '“I might need it someday.”',
  '“It still works.”',
  '“I paid too much for it.”',
  '“I could sell it.”',
  '“Someone gave it to me.”',
  '“I’ll repair it later.”',
  '“I’ll use it for a project.”',
  '“Maybe someone in the family wants it.”',
]

export const fullSpaces = [
  'The closet is full.',
  'The drawers are full.',
  'The kitchen cabinets are full.',
  'The garage has boxes waiting for a decision.',
]

export const realQuestions = [
  'Do I really use this?',
  'Is this a useful backup or just another duplicate?',
  'Should I sell it or donate it?',
  'Is “just in case” a real reason to keep it?',
  'What should I do with sentimental items?',
  'Is this project actually going to happen?',
  'Where should the item go after I make the decision?',
  'How do I prevent the same clutter from coming back?',
]

/* ---------- How it works ---------- */
export const usageSteps = [
  {
    number: '01',
    title: 'Choose a small category',
    text: 'T-shirts. Shoes. Mugs. Cables. Books. Papers. Hobby supplies. Sentimental items.',
    tail: 'Start small.',
  },
  {
    number: '02',
    title: 'Select up to 10 items',
    text: 'You do not need to empty the entire room.',
    tail: 'Work with what is directly in front of you.',
  },
  {
    number: '03',
    title: 'Follow the decision questions',
    text: 'Use, condition, duplicates, real future need, available space, value, and next action are already built into the system.',
  },
  {
    number: '04',
    title: 'Give every item a destination',
    text: 'KEEP · SELL · DONATE · GIVE AWAY · RECYCLE · DISCARD',
    tail: 'Then move forward.',
  },
]

export const decisions = ['KEEP', 'SELL', 'DONATE', 'GIVE AWAY', 'RECYCLE', 'DISCARD']

export const systemPath = [
  'Clothing',
  'Kitchen & Bathroom',
  'Electronics & Papers',
  'Sentimental Items',
  'Physical Exit',
  'Smarter Future Purchases',
]

export const categoryLoop = ['Identify', 'Compare', 'Decide', 'Assign', 'Act', 'Close the Loop']

/* ---------- Built for real homes ---------- */
export const roomNeeds = [
  'Your closet needs one kind of decision.',
  'Your kitchen needs another.',
  'Old electronics require more care.',
  'Sentimental belongings cannot be treated like ordinary objects.',
  'And a donation bag needs a completely different action than something you want to keep.',
]

export const homeSteps = [
  ['1', 'Identify', 'Know exactly what category you are working on.'],
  ['2', 'Compare', 'Check use, condition, duplicates, purpose, and available space.'],
  ['3', 'Decide', 'Choose what happens to the item.'],
  ['4', 'Complete the action', 'Store it intentionally or give it a real exit plan.'],
] as const

/* ---------- Volumes ---------- */
export type VolumeIcon = 'shirt' | 'home' | 'laptop' | 'heart' | 'recycle'

export const modules: {
  volume: string
  icon: VolumeIcon
  color: string
  name: string
  intro: string
  transforms?: [string, string][]
  content: string[]
  closing: string[]
}[] = [
  {
    volume: 'Volume 01',
    icon: 'shirt',
    color: 'bg-neutral-900',
    name: 'Clothing, Shoes & Accessories',
    intro: 'Make clearer decisions about what is taking up space in your closet.',
    content: [
      'T-shirts & tops',
      'Pants & jeans',
      'Dresses & occasion clothing',
      'Jackets & coats',
      'Workout clothing',
      'Clothes that no longer fit',
      'New clothes with tags',
      'Damaged clothing',
      'Sentimental clothing',
      'Shoes',
      'Handbags',
      'Backpacks',
      'Jewelry & accessories',
      'Duplicates',
      '“Just in case” clothing',
      'Repair decisions',
      'Selling decisions',
    ],
    closing: ['You will know what to compare before deciding what deserves closet space.'],
  },
  {
    volume: 'Volume 02',
    icon: 'home',
    color: 'bg-emerald-800',
    name: 'Kitchen, Bathroom & Everyday Items',
    intro: 'Create more functional everyday spaces by reviewing what you actually use.',
    content: [
      'Mugs & glasses',
      'Plates & bowls',
      'Cutlery',
      'Food containers',
      'Pots & pans',
      'Kitchen utensils',
      'Small appliances',
      'Water bottles',
      'Towels',
      'Personal-care products',
      'Makeup',
      'Perfume',
      'Hair tools',
      'Organizers',
      'Candles',
      'Small décor',
      'Cleaning products',
      'Household tools',
      'Duplicate items',
      'Available-space limits',
    ],
    closing: ['Practical questions help you separate “it still works” from “I actually need it.”'],
  },
  {
    volume: 'Volume 03',
    icon: 'laptop',
    color: 'bg-sky-800',
    name: 'Electronics, Hobbies, Books & Papers',
    intro: 'Turn drawers, boxes, shelves, and unfinished projects into clear decisions.',
    content: [
      'Mystery cables',
      'Chargers',
      'Old phones',
      'Tablets',
      'Computers',
      'Headphones',
      'Remote controls',
      'Adapters',
      'Electronics boxes',
      'Manuals',
      'Batteries',
      'Books',
      'Magazines',
      'Notebooks',
      'Receipts',
      'Warranties',
      'Sensitive papers',
      'Art supplies',
      'Fabric & yarn',
      'Craft tools',
      'Unfinished projects',
      'Future-project materials',
      'Collections',
      'Office supplies',
    ],
    closing: ['Identify first. Protect important information. Then decide.'],
  },
  {
    volume: 'Volume 04',
    icon: 'heart',
    color: 'bg-stone-600',
    name: 'Sentimental & Hard-to-Let-Go Items',
    intro:
      'A more respectful system for belongings connected to memories, people, and important moments.',
    content: [
      'Gifts',
      'Inherited items',
      'Clothing from loved ones',
      'Letters',
      'Cards',
      'Photographs',
      'Photo albums',
      'School memories',
      'Children’s artwork',
      'Baby clothes',
      'Childhood toys',
      'Souvenirs',
      'Tickets',
      'Trophies',
      'Past relationships',
      'Handmade gifts',
      'Family collections',
      'Inherited furniture',
      'Handwritten recipes',
      'Pet keepsakes',
      'Wedding items',
      'Memory boxes',
    ],
    closing: [
      'Keep the object, preserve the story, photograph it, share it, or review it later.',
      'The decision remains yours.',
    ],
  },
  {
    volume: 'Volume 05',
    icon: 'recycle',
    color: 'bg-blue-950',
    name: 'Sell, Donate, Recycle or Remove',
    intro: 'A decision is not complete while the item is still sitting inside your home.',
    transforms: [
      ['“I’m going to sell it.”', 'an actual listing.'],
      ['“I’m going to donate it.”', 'an actual donation.'],
      ['“I need to recycle this.”', 'an actual exit plan.'],
    ],
    content: [
      'Selling decisions',
      'Minimum selling threshold',
      'Simple sale photos',
      'Listing deadlines',
      'Maximum selling windows',
      'Bundles',
      'Donation destinations',
      'Give-away plans',
      'Electronics exit',
      'Textile exit',
      'Paper exit',
      'Special waste',
      'Large items',
      'Store returns',
      'Borrowed items',
      'Weekly Exit Day',
      'Plan B when the first exit fails',
    ],
    closing: ['Decision + physical exit = closed task.'],
  },
]

/* ---------- A look inside ---------- */
export type InsideBlock =
  | { type: 'qa'; q: string; a?: string }
  | { type: 'field'; label: string }
  | { type: 'checks'; label?: string; options: string[] }

export const insidePages: {
  title: string
  emoji: string
  item?: string
  blocks: InsideBlock[]
  resultLabel?: string
  resultNote?: string
  outcomes?: string[]
  footnote?: string
}[] = [
  {
    title: 'Closet Decision',
    emoji: '👕',
    item: 'Black jacket',
    blocks: [
      { type: 'qa', q: 'Do I wear it?', a: 'Yes' },
      { type: 'qa', q: 'Does it fit comfortably?', a: 'Yes' },
      { type: 'qa', q: 'Do I own something I choose more often?', a: 'Yes' },
      { type: 'qa', q: 'Does it serve a different function?', a: 'No' },
    ],
    resultLabel: 'Next decision:',
    resultNote: 'Compare both jackets directly.',
    outcomes: ['KEEP', 'SELL', 'DONATE', 'GIVE AWAY'],
  },
  {
    title: 'Cable Decision',
    emoji: '🔌',
    blocks: [
      { type: 'field', label: 'What does this cable connect to?' },
      { type: 'qa', q: 'Device identified?', a: 'Yes / No' },
      { type: 'qa', q: 'Do I still own that device?', a: 'Yes / No' },
      { type: 'qa', q: 'Is this the primary cable or a real backup?', a: 'Primary / Backup / Unknown' },
    ],
    resultLabel: 'If kept:',
    outcomes: ['LABEL IT.'],
  },
  {
    title: '“Just in Case” Check',
    emoji: '📦',
    blocks: [
      { type: 'field', label: 'What exact situation am I saving this for?' },
      { type: 'qa', q: 'Likelihood:', a: 'LOW · MEDIUM · HIGH' },
      { type: 'qa', q: 'Can I replace it easily?', a: 'YES · NO' },
      { type: 'qa', q: 'Do I already own another solution?', a: 'YES · NO' },
      { type: 'qa', q: 'Does it have a real place?', a: 'YES · NO' },
    ],
  },
  {
    title: 'Sentimental Decision',
    emoji: '❤️',
    blocks: [
      { type: 'field', label: 'What does this represent to me?' },
      {
        type: 'checks',
        label: 'Do I want:',
        options: [
          'The physical object',
          'The story',
          'The photograph',
          'The handwriting',
          'The family connection',
        ],
      },
    ],
    resultLabel: 'Possible next steps:',
    outcomes: ['KEEP', 'PHOTOGRAPH', 'DIGITIZE', 'SHARE', 'REVIEW LATER'],
  },
  {
    title: 'Exit Plan',
    emoji: '🚪',
    blocks: [
      { type: 'field', label: 'Item' },
      { type: 'field', label: 'Destination' },
      { type: 'checks', options: ['Sell', 'Donate', 'Give Away', 'Recycle', 'Return', 'Discard'] },
      { type: 'field', label: 'Exit date' },
    ],
    footnote: 'The process is complete when the item actually leaves.',
  },
]

/* ---------- Bonuses ---------- */
export const bonuses: {
  number: string
  emoji: string
  name: string
  description: string
  groups: { label?: string; items: string[]; style: 'chips' | 'list' | 'quotes' }[]
  highlights?: string[]
  value: number
  featured?: boolean
}[] = [
  {
    number: '01',
    emoji: '✅',
    name: '10 Decisions a Day',
    description:
      'A 30-day system built around small daily decisions. Instead of taking apart an entire room, you work with just 10 items.',
    groups: [
      {
        label: 'Includes daily categories such as:',
        style: 'chips',
        items: [
          'T-shirts',
          'Shoes',
          'Bags',
          'Mugs',
          'Food containers',
          'Towels',
          'Bathroom products',
          'Cables',
          'Chargers',
          'Papers',
          'Books',
          'Décor',
          'Hobby supplies',
          'Broken items',
          'Duplicates',
          'Sell pile',
          'Donation pile',
          'Sentimental items',
        ],
      },
    ],
    highlights: ['30 days × 10 items = up to 300 decisions.', 'Not 300 forced removals.'],
    value: bonusValues[0],
    featured: true,
  },
  {
    number: '02',
    emoji: '🏷️',
    name: 'Sell It or Donate It?',
    description:
      'Know when selling is worth your time — and when a faster exit makes more sense.',
    groups: [
      {
        label: 'Evaluate:',
        style: 'chips',
        items: [
          'Probable value',
          'Demand',
          'Condition',
          'Preparation time',
          'Photos',
          'Messages',
          'Delivery',
          'Storage while waiting',
        ],
      },
      {
        label: 'Includes:',
        style: 'chips',
        items: [
          'Clothing',
          'Shoes',
          'Electronics',
          'Appliances',
          'Furniture',
          'Books',
          'Collectibles',
          'Toys',
          'Hobby supplies',
          'Sports equipment',
          'Instruments',
          'Low-value bundles',
          'Bulky items',
        ],
      },
    ],
    highlights: ['Probable value − time − effort − difficulty = Is it worth selling?'],
    value: bonusValues[1],
  },
  {
    number: '03',
    emoji: '📦',
    name: '“Just in Case”',
    description:
      'A practical system for the objects you keep because you might need them someday.',
    groups: [
      {
        label: 'Work through thoughts like:',
        style: 'quotes',
        items: [
          'I’ll use it someday.',
          'It can be a backup.',
          'They don’t make this anymore.',
          'It was expensive.',
          'It’s brand new.',
          'It still works.',
          'Someone gave it to me.',
          'I’ll sell it.',
          'I’ll repair it.',
          'It’s for a future project.',
          'Someone could use it.',
          'It’s for guests.',
          'It might fit again.',
          'I’ll go back to this hobby.',
          'I’ll read it.',
        ],
      },
    ],
    highlights: ['Turn vague future possibilities into clear decisions.'],
    value: bonusValues[2],
  },
  {
    number: '04',
    emoji: '🚪',
    name: 'Exit Day',
    description:
      'A practical action system for moving already-decided items out of your home.',
    groups: [
      {
        label: 'On Exit Day, you focus only on:',
        style: 'chips',
        items: [
          'Donations',
          'Returns',
          'Buyer handoffs',
          'Give-away items',
          'Recycling',
          'Electronics',
          'Books',
          'Textiles',
          'Large items',
          'Special disposal',
        ],
      },
      {
        label: 'Includes:',
        style: 'list',
        items: [
          'One exit zone',
          'Destination planning',
          'Packing checks',
          'Transportation',
          'Plan B',
          'Weekly exit review',
          'Final exit checklist',
        ],
      },
    ],
    highlights: ['Today is for movement, not more sorting.'],
    value: bonusValues[3],
  },
  {
    number: '05',
    emoji: '🛒',
    name: 'Before You Buy',
    description: 'Protect the space you worked to recover.',
    groups: [
      {
        label: 'Before another item enters your home, ask:',
        style: 'list',
        items: [
          'Do I already own something that does this?',
          'Where will this live?',
          'When will I use it?',
          'What problem does it solve?',
          'Would I buy it without the sale?',
          'Can I wait 72 hours?',
        ],
      },
      {
        label: 'Includes practical decision systems for:',
        style: 'chips',
        items: [
          'Clothing',
          'Shoes',
          'Beauty products',
          'Kitchen tools',
          'Organizers',
          'Electronics',
          'New hobbies',
          'Books',
          'Stationery',
          'Collectibles',
          'Souvenirs',
          'Replacements',
          'Upgrades',
        ],
      },
    ],
    highlights: ['Fewer automatic purchases = fewer difficult decisions later.'],
    value: bonusValues[4],
  },
]

/* ---------- Offer ---------- */
export const included = [
  'Volume 1 — Clothing, Shoes & Accessories',
  'Volume 2 — Kitchen, Bathroom & Everyday Items',
  'Volume 3 — Electronics, Hobbies, Books & Papers',
  'Volume 4 — Sentimental & Hard-to-Let-Go Items',
  'Volume 5 — Sell, Donate, Recycle or Remove',
  'BONUS 1 — 10 Decisions a Day',
  'BONUS 2 — Sell It or Donate It?',
  'BONUS 3 — “Just in Case”',
  'BONUS 4 — Exit Day',
  'BONUS 5 — Before You Buy',
  'Digital PDF access',
  '7-day guarantee',
]

/* ---------- Testimonials ---------- */
export const testimonials = [
  {
    name: 'Jennifer M.',
    age: 42,
    role: 'Mom of three',
    location: 'Austin, Texas',
    title: 'The donation bags finally left my car.',
    quote:
      'I had four donation bags riding around in my trunk for almost a year. Volume 05 and the Exit Day bonus made me pick a destination and a date for each one. They were gone that same Saturday. What surprised me most is that I didn’t feel pressured to throw everything out — KEEP is allowed, and that made it so much easier to start.',
  },
  {
    name: 'Linda R.',
    age: 58,
    role: 'Empty nester',
    location: 'Columbus, Ohio',
    title: 'I could finally go through my mother’s things.',
    quote:
      'After my mom passed, her boxes sat in our guest room for two years. I couldn’t open them without crying. Volume 04 asks what the item represents and whether I want the object or the story — and that changed everything. I photographed a lot, kept the pieces that truly matter, and shared the rest with my sisters. It felt respectful, not cold.',
  },
  {
    name: 'Sarah K.',
    age: 36,
    role: 'Works from home',
    location: 'Manchester, UK',
    title: '10 decisions a day actually works for me.',
    quote:
      'Every declutter plan I tried wanted me to empty a whole room in a weekend. I would burn out by noon. The 10 Decisions a Day challenge is the opposite: ten items, ten decisions, then stop. I’ve been doing it for three weeks and my kitchen cabinets close properly for the first time since we moved in.',
  },
  {
    name: 'Michelle T.',
    age: 47,
    role: 'Nurse & mom of two',
    location: 'Toronto, Canada',
    title: 'My “just in case” drawer is gone.',
    quote:
      'I am the queen of “it still works.” Old chargers, mystery cables, three hand mixers… The cable decision page and the “Just in Case” bonus ask what exact situation I’m saving it for and whether I could replace it easily. Honest answers, quick decisions. I recycled two full boxes of electronics and labeled what we kept.',
  },
  {
    name: 'Karen D.',
    age: 52,
    role: 'Downsizing to a smaller home',
    location: 'Phoenix, Arizona',
    title: 'It stopped the clutter from coming back.',
    quote:
      'We were moving into a smaller house and I was terrified of filling it up again. The Before You Buy checklist lives on my phone now. “Where will this live?” and “Can I wait 72 hours?” have stopped so many impulse purchases. My husband says this is the first organizing thing I’ve bought that actually reduced what we own.',
  },
]

export const recentBuyers = [
  { name: 'Emily', place: 'Chicago, USA' },
  { name: 'Valentina', place: 'Buenos Aires, Argentina' },
  { name: 'Charlotte', place: 'London, UK' },
  { name: 'Sophie', place: 'Sydney, Australia' },
  { name: 'Hannah', place: 'Vancouver, Canada' },
  { name: 'Laura', place: 'Madrid, Spain' },
  { name: 'Grace', place: 'Dublin, Ireland' },
  { name: 'Megan', place: 'Auckland, New Zealand' },
  { name: 'Isabella', place: 'Mexico City, Mexico' },
]

/* ---------- Final CTA & FAQ ---------- */
export const finalQuestions = [
  'Why is this still here?',
  'Do I actually use it?',
  'Is it a duplicate?',
  'Am I keeping it for a real reason or a vague “someday”?',
  'What happens next?',
]

export const footerSteps = [
  'Choose the category.',
  'Review the item.',
  'Make the decision.',
  'Give it a destination.',
]

export const faqs = [
  [
    'Is this only for people with extremely cluttered homes?',
    'No. CLEAR THE CLUTTER™ is designed for everyday household clutter: crowded closets, too many duplicates, old electronics, books, papers, unused purchases, sentimental belongings, donation piles, and similar physical items.',
  ],
  [
    'Do I have to get rid of a certain number of things?',
    'No. The system helps you make decisions. KEEP is also a valid decision. The goal is to understand why an item stays and where it belongs.',
  ],
  [
    'Do I have to empty an entire room?',
    'No. The main system encourages working with small categories and manageable groups of items.',
  ],
  [
    'What if I have trouble deciding what to keep?',
    'The volumes include category-specific questions that help you compare use, condition, duplicates, future need, available space, sentimental value, and next steps.',
  ],
  [
    'Does it include clothing?',
    'Yes. Volume 01 is dedicated to clothing, shoes, bags, and accessories.',
  ],
  [
    'Does it include kitchen and bathroom clutter?',
    'Yes. Volume 02 covers everyday kitchen, bathroom, and household items.',
  ],
  [
    'What about old cables and electronics?',
    'Volume 03 includes cables, chargers, old devices, books, papers, hobbies, unfinished projects, and more.',
  ],
  [
    'Does it include sentimental belongings?',
    'Yes. Volume 04 provides a gentler decision system for gifts, inherited objects, photos, letters, family memories, keepsakes, and other sentimental items.',
  ],
  [
    'Does it tell me what to do with items once I decide they should leave?',
    'Yes. Volume 05 focuses specifically on selling, donating, giving away, recycling, returns, and completing the physical exit.',
  ],
  [
    'What if I always keep things “just in case”?',
    'Bonus 03 is dedicated entirely to that type of decision.',
  ],
  [
    'What if my donation bags never actually leave the house?',
    'Bonus 04 — Exit Day focuses on completing those exits.',
  ],
  [
    'Does it help prevent the clutter from coming back?',
    'Yes. Bonus 05 — Before You Buy provides a pre-purchase decision system for new physical items entering the home.',
  ],
  [
    'Is this a cleaning program?',
    'No. CLEAR THE CLUTTER™ is about physical belongings and decision-making, not cleaning schedules or housekeeping.',
  ],
  [
    'Is this treatment for hoarding disorder?',
    'No. This material is designed for everyday household clutter and decision-making. It is not a treatment for hoarding disorder or a substitute for professional mental-health support.',
  ],
  [
    'How do I receive the material?',
    'After purchase, you receive digital access to the PDFs.',
  ],
  [
    'Do I have lifetime access?',
    'Yes. Once purchased, you can keep the digital material and return to the system whenever you need it.',
  ],
  [
    'How does the guarantee work?',
    'You have 7 days to review the product. If it is not what you expected, contact COMPRADIGITALYAA@GMAIL.COM within the guarantee period.',
  ],
] as const
